import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import type { Governorate, Directorate, Restaurant } from "../../types";
import { UNIT_TYPE_LABELS } from "../../types";
import ItemsTab from "./ItemsTab";

type Tab = "items" | "governorates" | "directorates" | "restaurants";

interface GovForm { nameAr: string; sortOrder: string }
interface DirForm { governorateId: string; nameAr: string; unitType: string; code: string; sortOrder: string }
interface RestForm { governorateId: string; name: string }

const emptyGov: GovForm = { nameAr: "", sortOrder: "" };
const emptyDir = (governorateId = ""): DirForm => ({ governorateId, nameAr: "", unitType: "DIRECTORATE", code: "", sortOrder: "" });
const emptyRest = (governorateId = ""): RestForm => ({ governorateId, name: "" });

export default function DictionariesPage() {
  const [tab, setTab] = useState<Tab>("governorates");
  const qc = useQueryClient();
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  const [govMode, setGovMode] = useState<"new" | number | null>(null);
  const [govForm, setGovForm] = useState<GovForm>(emptyGov);

  const [dirMode, setDirMode] = useState<"new" | number | null>(null);
  const [dirForm, setDirForm] = useState<DirForm>(emptyDir());
  const [dirGovFilter, setDirGovFilter] = useState("");

  const [restMode, setRestMode] = useState<"new" | number | null>(null);
  const [restForm, setRestForm] = useState<RestForm>(emptyRest());
  const [restGovFilter, setRestGovFilter] = useState("");

  const { data: govs }  = useQuery({ queryKey: ["govs-all"],  queryFn: () => api.get<{ data: Governorate[] }>("/governorates").then(r => r.data.data) });
  const { data: dirs }  = useQuery({ queryKey: ["dirs-all"],  queryFn: () => api.get<{ data: Directorate[] }>("/directorates").then(r => r.data.data) });
  const { data: rests } = useQuery({ queryKey: ["rests-all"], queryFn: () => api.get<{ data: Restaurant[]  }>("/restaurants").then(r => r.data.data) });

  const doAction = async (cb: () => Promise<unknown>, keys: string[], okMsg = "تمت العملية بنجاح") => {
    setError(""); setSuccess("");
    try {
      await cb();
      keys.forEach(k => qc.invalidateQueries({ queryKey: [k] }));
      setSuccess(okMsg);
      return true;
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? "حدث خطأ");
      return false;
    }
  };

  const closeAllForms = () => { setGovMode(null); setDirMode(null); setRestMode(null); };

  // ── المحافظات ──────────────────────────────────────────────────────────
  const openNewGov  = () => { setGovMode("new"); setGovForm(emptyGov); };
  const openEditGov = (g: Governorate) => { setGovMode(g.id); setGovForm({ nameAr: g.nameAr, sortOrder: String(g.sortOrder) }); };
  const submitGov = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { nameAr: govForm.nameAr.trim(), sortOrder: govForm.sortOrder ? parseInt(govForm.sortOrder, 10) : 0 };
    const ok = govMode === "new"
      ? await doAction(() => api.post("/governorates", payload), ["govs-all"], "تمت إضافة المحافظة")
      : await doAction(() => api.put(`/governorates/${govMode}`, payload), ["govs-all"], "تم تعديل المحافظة");
    if (ok) setGovMode(null);
  };
  const deleteGov = (g: Governorate) => {
    if (!confirm(`حذف محافظة "${g.nameAr}" نهائياً؟ يشترط ألا يتبعها أي مديرية أو مطعم.`)) return;
    void doAction(() => api.delete(`/governorates/${g.id}`), ["govs-all"], "تم حذف المحافظة");
  };

  // ── المديريات ──────────────────────────────────────────────────────────
  const openNewDir  = () => { setDirMode("new"); setDirForm(emptyDir(dirGovFilter)); };
  const openEditDir = (d: Directorate) => {
    setDirMode(d.id);
    setDirForm({ governorateId: String(d.governorateId), nameAr: d.nameAr, unitType: d.unitType, code: d.code, sortOrder: String(d.sortOrder) });
  };
  const submitDir = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      governorateId: parseInt(dirForm.governorateId, 10), nameAr: dirForm.nameAr.trim(),
      unitType: dirForm.unitType, sortOrder: dirForm.sortOrder ? parseInt(dirForm.sortOrder, 10) : 0,
    };
    const ok = dirMode === "new"
      ? await doAction(() => api.post("/directorates", { ...payload, code: dirForm.code.trim() }), ["dirs-all"], "تمت إضافة المديرية")
      : await doAction(() => api.put(`/directorates/${dirMode}`, payload), ["dirs-all"], "تم تعديل المديرية");
    if (ok) setDirMode(null);
  };
  const toggleDirectorate = (d: Directorate) =>
    void doAction(() => api.put(`/directorates/${d.id}`, { isActive: !d.isActive }), ["dirs-all"],
      d.isActive ? "تم تعطيل المديرية" : "تم تفعيل المديرية");
  const deleteDir = (d: Directorate) => {
    if (!confirm(`حذف "${d.nameAr}" نهائياً؟ يشترط ألا تحتوي طلبات أو مستخدمين سابقين.`)) return;
    void doAction(() => api.delete(`/directorates/${d.id}`), ["dirs-all"], "تم حذف المديرية");
  };

  // ── المطاعم ────────────────────────────────────────────────────────────
  const openNewRest  = () => { setRestMode("new"); setRestForm(emptyRest(restGovFilter)); };
  const openEditRest = (r: Restaurant) => { setRestMode(r.id); setRestForm({ governorateId: String(r.governorateId), name: r.name }); };
  const submitRest = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { governorateId: parseInt(restForm.governorateId, 10), name: restForm.name.trim() };
    const ok = restMode === "new"
      ? await doAction(() => api.post("/restaurants", payload), ["rests-all"], "تمت إضافة المطعم")
      : await doAction(() => api.put(`/restaurants/${restMode}`, payload), ["rests-all"], "تم تعديل المطعم");
    if (ok) setRestMode(null);
  };
  const toggleRestaurant = (r: Restaurant) =>
    void doAction(() => api.put(`/restaurants/${r.id}`, { isActive: !r.isActive }), ["rests-all"],
      r.isActive ? "تم تعطيل المطعم" : "تم تفعيل المطعم");
  const deleteRest = (r: Restaurant) => {
    if (!confirm(`حذف مطعم "${r.name}" نهائياً؟`)) return;
    void doAction(() => api.delete(`/restaurants/${r.id}`), ["rests-all"], "تم حذف المطعم");
  };

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: "items",         label: "الأصناف" },
    { key: "governorates",  label: "المحافظات" },
    { key: "directorates",  label: "المديريات والأفرع" },
    { key: "restaurants",   label: "المطاعم" },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">إدارة القواميس</h1>
        <p className="page-subtitle">الأصناف — المحافظات — المديريات والأفرع — المطاعم</p>
      </div>

      {error   && <div className="alert-error   mb-4">{error}</div>}
      {success && <div className="alert-success mb-4">{success}</div>}

      <div className="flex gap-1 mb-4 border-b border-sand-300">
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => { setTab(t.key); setError(""); setSuccess(""); closeAllForms(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
              tab === t.key ? "border-primary-700 text-primary-700" : "border-transparent text-sand-500 hover:text-gray-900"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════════ المحافظات ═══════════════ */}
      {tab === "governorates" && (
        <>
          <div className="flex justify-end mb-3">
            <button onClick={openNewGov} className="btn-primary text-sm">+ محافظة جديدة</button>
          </div>

          {govMode === "new" && (
            <form onSubmit={submitGov} className="card mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="label">اسم المحافظة <span className="text-red-500">*</span></label>
                <input className="input" required value={govForm.nameAr}
                  onChange={e => setGovForm(f => ({ ...f, nameAr: e.target.value }))} placeholder="مثال: دمشق" />
              </div>
              <div>
                <label className="label">ترتيب العرض</label>
                <input type="number" min="0" className="input" value={govForm.sortOrder}
                  onChange={e => setGovForm(f => ({ ...f, sortOrder: e.target.value }))} placeholder="0" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary flex-1">حفظ</button>
                <button type="button" className="btn-secondary" onClick={() => setGovMode(null)}>إلغاء</button>
              </div>
            </form>
          )}

          <div className="table-container">
            <table className="table">
              <thead><tr><th>#</th><th>الاسم</th><th>الترتيب</th><th>عدد المديريات والأفرع</th><th>إجراء</th></tr></thead>
              <tbody>
                {(govs ?? []).map(g => (
                  <React.Fragment key={g.id}>
                    <tr>
                      <td className="text-sand-400">{g.id}</td>
                      <td className="font-medium">{g.nameAr}</td>
                      <td>{g.sortOrder}</td>
                      <td>{(dirs ?? []).filter(d => d.governorateId === g.id && d.isActive).length}</td>
                      <td className="whitespace-nowrap">
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => openEditGov(g)}>تعديل</button>
                        <button className="text-xs text-[#6b1f2a] hover:underline" onClick={() => deleteGov(g)}>حذف</button>
                      </td>
                    </tr>
                    {govMode === g.id && (
                      <tr><td colSpan={5}>
                        <form onSubmit={submitGov} className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="label">اسم المحافظة</label>
                            <input className="input" required value={govForm.nameAr}
                              onChange={e => setGovForm(f => ({ ...f, nameAr: e.target.value }))} />
                          </div>
                          <div>
                            <label className="label">ترتيب العرض</label>
                            <input type="number" min="0" className="input" value={govForm.sortOrder}
                              onChange={e => setGovForm(f => ({ ...f, sortOrder: e.target.value }))} />
                          </div>
                          <div className="flex gap-2">
                            <button type="submit" className="btn-primary flex-1">حفظ التعديل</button>
                            <button type="button" className="btn-secondary" onClick={() => setGovMode(null)}>إلغاء</button>
                          </div>
                        </form>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════════ المديريات ═══════════════ */}
      {tab === "directorates" && (
        <>
          <div className="flex flex-wrap justify-between items-end gap-3 mb-3">
            <div>
              <label className="label">تصفية حسب المحافظة</label>
              <select className="input w-48" value={dirGovFilter} onChange={e => setDirGovFilter(e.target.value)}>
                <option value="">كل المحافظات</option>
                {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
              </select>
            </div>
            <button onClick={openNewDir} className="btn-primary text-sm">+ مديرية/فرع جديد</button>
          </div>

          {dirMode === "new" && (
            <form onSubmit={submitDir} className="card mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div>
                <label className="label">المحافظة <span className="text-red-500">*</span></label>
                <select className="input" required value={dirForm.governorateId}
                  onChange={e => setDirForm(f => ({ ...f, governorateId: e.target.value }))}>
                  <option value="">-- اختر المحافظة --</option>
                  {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
                </select>
              </div>
              <div>
                <label className="label">الاسم بالعربية <span className="text-red-500">*</span></label>
                <input className="input" required value={dirForm.nameAr}
                  onChange={e => setDirForm(f => ({ ...f, nameAr: e.target.value }))} placeholder="مثال: مديرية الشؤون المالية" />
              </div>
              <div>
                <label className="label">النوع</label>
                <select className="input" value={dirForm.unitType} onChange={e => setDirForm(f => ({ ...f, unitType: e.target.value }))}>
                  <option value="DIRECTORATE">مديرية</option>
                  <option value="BRANCH">فرع</option>
                  <option value="SECTION">قسم</option>
                </select>
              </div>
              <div>
                <label className="label">الكود (بالإنجليزية) <span className="text-red-500">*</span></label>
                <input className="input" required value={dirForm.code}
                  onChange={e => setDirForm(f => ({ ...f, code: e.target.value }))} placeholder="مثال: Financial" />
              </div>
              <div>
                <label className="label">ترتيب العرض</label>
                <input type="number" min="0" className="input" value={dirForm.sortOrder}
                  onChange={e => setDirForm(f => ({ ...f, sortOrder: e.target.value }))} />
              </div>
              <div className="sm:col-span-2 lg:col-span-5 flex justify-end gap-2">
                <button type="button" className="btn-secondary" onClick={() => setDirMode(null)}>إلغاء</button>
                <button type="submit" className="btn-primary">حفظ</button>
              </div>
            </form>
          )}

          <div className="table-container">
            <table className="table">
              <thead><tr><th>#</th><th>الاسم</th><th>المحافظة</th><th>النوع</th><th>الكود</th><th>الترتيب</th><th>الحالة</th><th>إجراء</th></tr></thead>
              <tbody>
                {(dirs ?? []).filter(d => !dirGovFilter || d.governorateId === Number(dirGovFilter)).map(d => (
                  <React.Fragment key={d.id}>
                    <tr>
                      <td className="text-sand-400">{d.id}</td>
                      <td className="font-medium">{d.nameAr}</td>
                      <td>{d.governorate?.nameAr}</td>
                      <td><span className="text-xs text-sand-500">{UNIT_TYPE_LABELS[d.unitType]}</span></td>
                      <td><code className="text-xs bg-sand-50 px-1 rounded" dir="ltr">{d.code}</code></td>
                      <td>{d.sortOrder}</td>
                      <td><span className={d.isActive ? "text-primary-700" : "text-[#6b1f2a]"}>{d.isActive ? "فعّال" : "غير فعّال"}</span></td>
                      <td className="whitespace-nowrap">
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => openEditDir(d)}>تعديل</button>
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => toggleDirectorate(d)}>{d.isActive ? "تعطيل" : "تفعيل"}</button>
                        <button className="text-xs text-[#6b1f2a] hover:underline" onClick={() => deleteDir(d)}>حذف</button>
                      </td>
                    </tr>
                    {dirMode === d.id && (
                      <tr><td colSpan={8}>
                        <form onSubmit={submitDir} className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                          <div>
                            <label className="label">المحافظة</label>
                            <select className="input" required value={dirForm.governorateId}
                              onChange={e => setDirForm(f => ({ ...f, governorateId: e.target.value }))}>
                              {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="label">الاسم بالعربية</label>
                            <input className="input" required value={dirForm.nameAr}
                              onChange={e => setDirForm(f => ({ ...f, nameAr: e.target.value }))} />
                          </div>
                          <div>
                            <label className="label">النوع</label>
                            <select className="input" value={dirForm.unitType} onChange={e => setDirForm(f => ({ ...f, unitType: e.target.value }))}>
                              <option value="DIRECTORATE">مديرية</option>
                              <option value="BRANCH">فرع</option>
                              <option value="SECTION">قسم</option>
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <button type="submit" className="btn-primary flex-1">حفظ التعديل</button>
                            <button type="button" className="btn-secondary" onClick={() => setDirMode(null)}>إلغاء</button>
                          </div>
                        </form>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════════ الأصناف ═══════════════ */}
      {tab === "items" && <ItemsTab />}

      {/* ═══════════════ المطاعم ═══════════════ */}
      {tab === "restaurants" && (
        <>
          <div className="flex flex-wrap justify-between items-end gap-3 mb-3">
            <div>
              <label className="label">تصفية حسب المحافظة</label>
              <select className="input w-48" value={restGovFilter} onChange={e => setRestGovFilter(e.target.value)}>
                <option value="">كل المحافظات</option>
                {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
              </select>
            </div>
            <button onClick={openNewRest} className="btn-primary text-sm">+ مطعم جديد</button>
          </div>

          {restMode === "new" && (
            <form onSubmit={submitRest} className="card mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="label">المحافظة <span className="text-red-500">*</span></label>
                <select className="input" required value={restForm.governorateId}
                  onChange={e => setRestForm(f => ({ ...f, governorateId: e.target.value }))}>
                  <option value="">-- اختر المحافظة --</option>
                  {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
                </select>
              </div>
              <div>
                <label className="label">اسم المطعم <span className="text-red-500">*</span></label>
                <input className="input" required value={restForm.name}
                  onChange={e => setRestForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary flex-1">حفظ</button>
                <button type="button" className="btn-secondary" onClick={() => setRestMode(null)}>إلغاء</button>
              </div>
            </form>
          )}

          <div className="table-container">
            <table className="table">
              <thead><tr><th>#</th><th>الاسم</th><th>المحافظة</th><th>الحالة</th><th>إجراء</th></tr></thead>
              <tbody>
                {(rests ?? []).filter(r => !restGovFilter || r.governorateId === Number(restGovFilter)).length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-sand-400 py-6">لا توجد مطاعم مسجّلة — أضِف مطعماً بالزر أعلاه</td></tr>
                ) : (rests ?? []).filter(r => !restGovFilter || r.governorateId === Number(restGovFilter)).map(r => (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td className="text-sand-400">{r.id}</td>
                      <td className="font-medium">{r.name}</td>
                      <td>{r.governorate?.nameAr ?? "—"}</td>
                      <td><span className={r.isActive ? "text-primary-700" : "text-[#6b1f2a]"}>{r.isActive ? "فعّال" : "غير فعّال"}</span></td>
                      <td className="whitespace-nowrap">
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => openEditRest(r)}>تعديل</button>
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => toggleRestaurant(r)}>{r.isActive ? "تعطيل" : "تفعيل"}</button>
                        <button className="text-xs text-[#6b1f2a] hover:underline" onClick={() => deleteRest(r)}>حذف</button>
                      </td>
                    </tr>
                    {restMode === r.id && (
                      <tr><td colSpan={5}>
                        <form onSubmit={submitRest} className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="label">المحافظة</label>
                            <select className="input" required value={restForm.governorateId}
                              onChange={e => setRestForm(f => ({ ...f, governorateId: e.target.value }))}>
                              {(govs ?? []).map(g => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="label">اسم المطعم</label>
                            <input className="input" required value={restForm.name}
                              onChange={e => setRestForm(f => ({ ...f, name: e.target.value }))} />
                          </div>
                          <div className="flex gap-2">
                            <button type="submit" className="btn-primary flex-1">حفظ التعديل</button>
                            <button type="button" className="btn-secondary" onClick={() => setRestMode(null)}>إلغاء</button>
                          </div>
                        </form>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
